import * as R from 'ramda';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { connect } from 'react-redux';
import Popper from '@mui/material/Popper';
import { createStructuredSelector } from 'reselect';
import { OuterClick } from 'react-outer-click';
import React, { useMemo, useState, Fragment, useCallback } from 'react';
// components
import { HoveringTitle } from '../hovering-title';
import { getAvailableFilterFields } from '../edit-report/helpers';
// features
import { makeSelectAvailableReportGeoFencingZoneList } from '../../features/geo-fencing-zone/report/selectors';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// forms
import { Form, Fieldset2 } from '../../forms';
// icons
import * as I from '../../svgs';
// ui
import { ActionButton, IconWrapper } from '../../ui';
// component filter
import { FilterWrapper } from './ui';
import { reportConditions, newReportConditions } from './settings';
//////////////////////////////////////////////////

const getConditionOptions = (props) => {
  const { type, dataType } = props;

  const newConditionReportTypes = [GC.CLO_REPORT, GC.TEL_REPORT];

  const conditionsFunc = G.ifElse(
    R.includes(type, newConditionReportTypes),
    newReportConditions,
    reportConditions,
  );

  return conditionsFunc()[R.or(dataType, 'default')];
};

const labelStyles = {
  display: 'none',
};

const inputWrapperStyles = {
  mb: 10,
  width: 230,
};

const whiteColor = G.getTheme('colors.white');
const darkBlueColor = G.getTheme('colors.dark.blue');

const rangeOperations = ['range', 'date-range'];

const validationSchema = Yup.lazy(({ operation }) => Yup.object().shape({
  filter: G.yupStringRequired,
  operation: G.yupStringRequired,
  to: G.ifElse(R.includes(operation, rangeOperations), G.yupStringRequired),
  from: G.ifElse(R.includes(operation, rangeOperations), G.yupStringRequired),
}));

const FilterPopper = (props) => {
  const {
    type,
    open,
    zIndex,
    position,
    filterBy,
    anchorEl,
    disablePortal,
    handleTogglePopper,
    shouldSubmitFilterForm,
  } = props;

  const onSubmit = useCallback((values) => {
    const {
      type,
      filterBy,
      filterAction,
      setFilterParam,
    } = props;

    const {
      to,
      from,
      last,
      next,
      value,
      filter,
      operation,
    } = values;

    const selectedField = R.or(R.find(R.propEq(filter, GC.FIELD_VALUE), filterBy), {});

    const { type: fieldDataType, uiType, referenceFieldName } = selectedField;

    const dataType = R.or(uiType, fieldDataType);
    const splittedDataType = R.head(R.split(':', dataType));
    const requestDataType = G.ifElse(R.includes(dataType, ['time', 'dateOnly']), 'date', splittedDataType);
    const keyName = `${splittedDataType}Value`;

    const fieldName = G.ifElse(
      R.equals(dataType, GC.FIELD_REFERENCE),
      GC.FIELD_REFERENCE_NAME,
      'propertyName',
    );

    const isDateRange = R.equals(operation, GC.FIELD_DATE_RANGE);

    let valueToUse = value;

    if (G.isArray(value)) {
      valueToUse = R.compose(
        R.filter((option) => R.includes(option.value, value)),
        R.path(['options']),
        R.find(R.propEq(filter, 'value')),
      )(filterBy);
    }

    let params = { [keyName]: valueToUse };

    if (R.equals(dataType, 'date')) {
      params = {
        last,
        next,
        dateRelative: R.not(isDateRange),
        operation: G.ifElse(isDateRange, operation, ''),
        timeUnit: G.ifElse(isDateRange, null, operation),
        to: G.convertInstanceToDefaultDateTimeFormat(to),
        from: G.convertInstanceToDefaultDateTimeFormat(from),
      };
    }

    if (R.equals(dataType, 'dateOnly')) {
      const getDateOnlyValue = (date) => (
        G.isNilOrEmpty(date)
          ? null
          : `${G.convertInstanceToDefaultDateFormat(date)} ${GC.DEFAULT_FILTER_TIME}`
      );
      const fromValue = getDateOnlyValue(from);

      params = {
        from: fromValue,
        to: G.ifElse(R.equals(operation, 'equal'), fromValue, getDateOnlyValue(to)),
      };
    }

    if (R.equals(dataType, 'time')) {
      const getTimeValue = (time) => {
        if (G.isNilOrEmpty(time)) return null;

        const valueToFormat = R.compose(R.join(' '), R.takeLast(2), R.split(' '))(time);

        return `${GC.DEFAULT_FILTER_DATE} ${G.convertTimeToFormat(valueToFormat, GC.DEFAULT_TIME_FORMAT)}`;
      };

      params = {
        to: getTimeValue(to),
        from: getTimeValue(from),
      };
    }

    const isRef = R.equals(dataType, GC.FIELD_REFERENCE);

    let reqBody = {
      operation,
      dateRelative: false,
      propertyName: G.ifElse(isRef, GC.FIELD_REFERENCES, ''),
      // NOTE: important
      [fieldName]: filter,
      ...params,
      dataType: requestDataType,
    };

    if (isRef) {
      reqBody = R.assoc(
        GC.FIELD_REFERENCE_FIELD_NAME,
        R.or(referenceFieldName, null),
        reqBody,
      );
    }

    if (G.isFilterNumberRange({ dataType, operation })) {
      reqBody = {
        ...reqBody,
        numberValue: null,
        numberRange: { to: G.toNumber(to), from: G.toNumber(from) },
      };
    }

    if (G.isFilterStringRange({ dataType, operation })) {
      reqBody = {
        ...reqBody,
        stringValue: null,
        stringRange: { to, from },
      };
    }

    if (G.isFilterValuesBoolean({ dataType, operation })) {
      reqBody = R.assoc(GC.FIELD_BOOLEAN_VALUE, G.isTrue(value), reqBody);
    }

    setFilterParam(reqBody);
    filterAction({ reqBody, type });
    handleTogglePopper();
  }, [filterBy]);

  const formik = useFormik({
    onSubmit,
    validationSchema,
    initialValues: {
      to: '',
      from: '',
      last: '',
      next: '',
      value: '',
      filter: null,
      operation: null,
    },
  });

  const { submitForm, handleSubmit, values: { filter, operation } } = formik;

  const handleClick = () => {
    if (shouldSubmitFilterForm) submitForm();
  };

  const fieldSettings = useMemo(() => {
    const filterByOptions = R.compose(
      R.map(({ name, value }) => ({ value, [GC.FIELD_LABEL]: name })),
      R.filter(({ disableFilter }) => R.not(disableFilter)),
    )(filterBy);

    const selectedField = R.or(R.find(R.propEq(filter, GC.FIELD_VALUE), filterBy), {});

    const { type: fieldDataType, uiType, options, placeholder } = selectedField;

    const dataType = R.or(uiType, fieldDataType);
    const placeholderText = G.getWindowLocale(...G.isObject(placeholder) ? R.values(placeholder) : ['titles:choose']);

    const makeFilterValueFieldSettings = () => {
      const dataTypeMap = {
        default: { type: 'text' },
        number: { type: 'number' },
        date: {
          type: 'datePicker',
          disablePortal: true,
        },
        time: {
          type: 'timeInput',
        },
        dateOnly: {
          type: 'datePicker',
          disablePortal: true,
        },
        boolean: {
          type: 'reactSelect',
          useMenuPortalTarget: false,
          placeholder: placeholderText,
          options: [
            {
              value: true,
              label: G.getWindowLocale('titles:yes', 'Yes'),
            },
            {
              value: false,
              label: G.getWindowLocale('titles:no', 'No'),
            },
          ],
        },
        'string:select': {
          options,
          type: 'reactSelect',
          useMenuPortalTarget: false,
          placeholder: placeholderText,
        },
        selectMultiple: {
          options,
          isMulti: true,
          type: 'reactSelect',
          useMenuPortalTarget: false,
          placeholder: placeholderText,
        },
      };

      const fieldOptions = R.prop(dataType, dataTypeMap);

      if (R.includes(operation, rangeOperations)) {
        const mb = G.ifElse(R.equals(operation, 'date-range'), 10);
        const width = G.ifElse(R.equals(operation, 'date-range'), 230, 110);

        return [
          {
            ...fieldOptions,
            labelStyles,
            errorWidth: '0px',
            fieldName: 'from',
            inputWrapperStyles: { mb, width },
            placeholder: G.getWindowLocale('titles:from', 'From'),
            maxDate: ({ values: { to } = {} }) => (
              G.isValidDate(to) ? new Date(to) : null
            ),
          },
          {
            ...fieldOptions,
            labelStyles,
            fieldName: 'to',
            errorWidth: '0px',
            inputWrapperStyles: { width },
            placeholder: G.getWindowLocale('titles:to', 'To'),
            minDate: ({ values: { from } = {} }) => (
              G.isValidDate(from) ? new Date(from) : null
            ),
          },
        ];
      }

      if (R.and(G.notEquals(operation, 'date-range'), R.equals(dataType, 'date'))) {
        return [
          {
            ...fieldOptions,
            type: 'number',
            errorWidth: '0px',
            fieldName: 'last',
            inputWrapperStyles: { width: 110 },
            placeholder: G.getWindowLocale('titles:last', 'Earlier'),
          },
          {
            ...fieldOptions,
            type: 'number',
            fieldName: 'next',
            errorWidth: '0px',
            inputWrapperStyles: { width: 110 },
            placeholder: G.getWindowLocale('titles:later', 'Later'),
          },
        ];
      }

      if (R.equals(dataType, 'dateOnly')) {
        return [
          {
            ...fieldOptions,
            labelStyles,
            errorWidth: '0px',
            fieldName: 'from',
            inputWrapperStyles: { width: 230 },
            placeholder: G.getWindowLocale('titles:select-date', 'Select Date'),
          },
        ];
      }

      return [
        {
          ...fieldOptions,
          labelStyles,
          errorWidth: '0px',
          fieldName: 'value',
          inputWrapperStyles: { width: 230 },
          placeholder: G.getWindowLocale('titles:enter-a-value', 'Enter a Value'),
        },
      ];
    };

    const handleFilterChange = (value, _, { values, setValues }) => {
      const nextField = R.or(R.find(R.propEq(value, GC.FIELD_VALUE), filterBy), {});
      const nextDataType = R.or(nextField.uiType, nextField.type);

      const { operation: prevOperation } = values;

      setValues({
        to: '',
        from: '',
        last: '',
        next: '',
        value: '',
        filter: value,
        operation: G.ifElse(
          R.equals(getConditionOptions({ type, dataType }), getConditionOptions({ type, dataType: nextDataType })),
          prevOperation,
          null,
        ),
      });
    };

    const settings = [
      {
        labelStyles,
        errorWidth: '0px',
        inputWrapperStyles,
        type: 'reactSelect',
        fieldName: 'filter',
        options: filterByOptions,
        shouldCustomChange: true,
        useMenuPortalTarget: false,
        customChangeHandler: handleFilterChange,
        placeholder: G.getWindowLocale('titles:type-to-search', 'Type to search'),

      },
      {
        labelStyles,
        errorWidth: '0px',
        inputWrapperStyles,
        type: 'reactSelect',
        fieldName: 'operation',
        useMenuPortalTarget: false,
        getOptionLabel: R.prop(GC.FIELD_NAME),
        options: getConditionOptions({ type, dataType }),
        placeholder: G.getWindowLocale('titles:condition', 'Condition'),
      },
      ...makeFilterValueFieldSettings(),
    ];

    return settings;
  }, [filter, filterBy, operation]);

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      disablePortal={disablePortal}
      sx={{ zIndex: G.ifElse(disablePortal, zIndex, 1300) }}
      placement={G.ifElse(position, position, 'bottom-end')}
    >
      <OuterClick as={FilterWrapper} onOuterClick={() => handleTogglePopper()}>
        <Form onSubmit={handleSubmit}>
          <Fieldset2
            {...formik}
            fields={fieldSettings}
            fieldsWrapperStyles={{ m: 10, width: 230, justifyContent: 'space-between' }}
          />
          <ActionButton
            m={10}
            height={32}
            fontSize={14}
            borderRadius='5px'
            bgColor={whiteColor}
            onClick={handleClick}
            textTransform='uppercase'
            textColor={darkBlueColor}
            border={`1px solid ${darkBlueColor}`}
            type={G.ifElse(shouldSubmitFilterForm, 'button', 'submit')}
          >
            {G.getWindowLocale('actions:find', 'Find')}
          </ActionButton>
        </Form>
      </OuterClick>
    </Popper>
  );
};

const Component = (props) => {
  const {
    version,
    usedReport,
    filterColor,
    geoFencingZones,
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);

  const handleTogglePopper = (event) => setAnchorEl(event ? event.currentTarget : null);

  const color = G.getThemeByCond(
    G.isThemeSecondVersion(version),
    'colors.dark.blue',
    'icons.iconColor',
  );

  const filterBy = getAvailableFilterFields({
    usedReport,
    geoFencingZones,
    availableFields: R.pathOr([], ['filterBy'], props),
  });

  return (
    <Fragment>
      <HoveringTitle
        handleClick={handleTogglePopper}
        title={G.getWindowLocale('titles:quick-filter', 'Quick Filter')}
        positionConfig={{
          zIndex: 14,
          right: '70%',
          bottom: '110%',
          width: 'max-content',
        }}
      >
        <IconWrapper
          onClick={handleTogglePopper}
          title={G.getWindowLocale('titles:quick-filter', 'Quick Filter')}
        >
          {I.filter(R.or(filterColor, color))}
        </IconWrapper>
      </HoveringTitle>
      <FilterPopper
        {...props}
        filterBy={filterBy}
        anchorEl={anchorEl}
        open={R.isNotNil(anchorEl)}
        handleTogglePopper={handleTogglePopper}
      />
    </Fragment>
  );
};

const mapStateToProps = (state) => createStructuredSelector({
  geoFencingZones: makeSelectAvailableReportGeoFencingZoneList(state),
});

export const Filter = connect(mapStateToProps)(Component);

export default Filter;
